import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { useUser } from '../context/UserContext';
import { useLocale } from '../context/DynamicLocaleProvider';
import { isInternalAdmin, isOrgAdmin } from '../utils/roles';
import type { UserRole } from '../types';
import { useTheme } from '../context/ThemeProvider';
import { useProfilePhotoUpload } from '../hooks/useProfilePhotoUpload';
import { APP_VERSION } from '../version';
import { LanguageSwitch } from './LanguageSwitch';
import { SiteFooter } from './SiteFooter';
import { ViewFadeIn } from './ViewFadeIn';
import { KpiExplainProvider } from './KpiExplainProvider';
import styles from './AppLayout.module.css';

const ROLE_LABEL_KEYS = {
  internal_admin: 'role_internal_admin',
  client_admin: 'role_client_admin',
  client_user: 'role_client_user',
  lender_admin: 'role_lender_admin',
} as const satisfies Record<UserRole, string>;

function headerAvatarInitials(
  firstName: string,
  lastName: string,
  email: string | null
): string {
  const f = firstName.trim();
  const l = lastName.trim();
  if (f && l) return `${f[0]!}${l[0]!}`.toUpperCase();
  if (f.length >= 2) return f.slice(0, 2).toUpperCase();
  if (f) return f.slice(0, 1).toUpperCase();
  if (email) {
    const local = email.split('@')[0] ?? '';
    if (local.length >= 2) return local.slice(0, 2).toUpperCase();
    if (local) return local.slice(0, 1).toUpperCase();
  }
  return '?';
}

function ThemeToggleIcon({ theme }: { theme: 'light' | 'dark' }) {
  if (theme === 'light') {
    return (
      <svg className={styles.themeToggleIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M20 14.5A8.5 8.5 0 0 1 9.5 4 7 7 0 1 0 20 14.5Z"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg className={styles.themeToggleIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function AppLayout() {
  const { theme, toggleTheme } = useTheme();
  const { pathname } = useLocation();
  const { user, email, firstName, lastName, avatar, role } = useUser();
  const { t } = useLocale();
  const { uploading: avatarUploading, upload: uploadAvatar } = useProfilePhotoUpload(user?.uid);
  const [avatarBroken, setAvatarBroken] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const isDarkMode = theme === 'dark';

  const nav: Array<{
    to: string;
    label: string;
    icon: string;
    count?: number;
    isActive: boolean;
  }> = isInternalAdmin(role)
    ? [
        {
          to: '/clients',
          label: t('nav_clients'),
          icon: isDarkMode ? '/assets/icons/building.2.svg' : '/assets/icons/building.2.black.svg',
          isActive: pathname.startsWith('/clients'),
        },
        {
          to: '/lenders',
          label: t('nav_lenders'),
          icon: isDarkMode
            ? '/assets/icons/point.3.connected.trianglepath.dotted.svg'
            : '/assets/icons/point.3.connected.trianglepath.dotted.black.svg',
          isActive: pathname.startsWith('/lenders'),
        },
        {
          to: '/financing-options',
          label: t('nav_financing_options'),
          icon: isDarkMode ? '/assets/icons/creditcard.svg' : '/assets/icons/creditcard.black.svg',
          isActive: pathname.startsWith('/financing-options'),
        },
        {
          to: '/team',
          label: t('nav_team'),
          icon: isDarkMode ? '/assets/icons/person.2.svg' : '/assets/icons/person.2.black.svg',
          isActive: pathname.startsWith('/team'),
        },
        {
          to: '/settings',
          label: t('nav_settings'),
          icon: isDarkMode ? '/assets/icons/gearshape.svg' : '/assets/icons/gearshape.black.svg',
          isActive: pathname.startsWith('/settings'),
        },
      ]
    : isOrgAdmin(role)
    ? [
        {
          to: '/home',
          label: t('nav_home'),
          icon: isDarkMode ? '/assets/icons/house.svg' : '/assets/icons/house.black.svg',
          isActive: pathname === '/' || pathname === '/home',
        },
        {
          to: '/cases',
          label: t('nav_cases'),
          icon: isDarkMode ? '/assets/icons/eurosign.svg' : '/assets/icons/eurosign.black.svg',
          isActive: pathname.startsWith('/cases'),
        },
        {
          to: '/documents',
          label: t('nav_documents'),
          icon: isDarkMode ? '/assets/icons/text.document.svg' : '/assets/icons/text.document.black.svg',
          isActive: pathname.startsWith('/documents'),
        },
        {
          to: '/customers',
          label: t('nav_customers'),
          icon: isDarkMode ? '/assets/icons/building.2.svg' : '/assets/icons/building.2.black.svg',
          isActive: pathname.startsWith('/customers'),
        },
        {
          to: '/installments',
          label: t('nav_installments'),
          icon: isDarkMode ? '/assets/icons/creditcard.svg' : '/assets/icons/creditcard.black.svg',
          isActive: pathname.startsWith('/installments'),
        },
        {
          to: '/branding',
          label: t('nav_branding'),
          icon: isDarkMode ? '/assets/icons/calendar.svg' : '/assets/icons/calendar.black.svg',
          isActive: pathname.startsWith('/branding'),
        },
        {
          to: '/audit',
          label: t('nav_audit'),
          icon: isDarkMode
            ? '/assets/icons/list.bullet.svg'
            : '/assets/icons/list.bullet.black.svg',
          isActive: pathname.startsWith('/audit'),
        },
        {
          to: '/team',
          label: t('nav_team'),
          icon: isDarkMode ? '/assets/icons/person.2.svg' : '/assets/icons/person.2.black.svg',
          isActive: pathname.startsWith('/team'),
        },
        {
          to: '/settings',
          label: t('nav_settings'),
          icon: isDarkMode ? '/assets/icons/gearshape.svg' : '/assets/icons/gearshape.black.svg',
          isActive: pathname.startsWith('/settings'),
        },
      ]
    : role === 'lender_admin'
    ? // Lender staff — no client workspace, only the team roster.
      [
        {
          to: '/team',
          label: t('nav_team'),
          icon: isDarkMode ? '/assets/icons/person.2.svg' : '/assets/icons/person.2.black.svg',
          isActive: pathname.startsWith('/team'),
        },
      ]
    : // client_user (e.g. end customers from /check) tracks their own
      // request on the overview and sees the team roster.
      [
        {
          to: '/home',
          label: t('nav_home'),
          icon: isDarkMode ? '/assets/icons/house.svg' : '/assets/icons/house.black.svg',
          isActive: pathname === '/' || pathname === '/home',
        },
        {
          to: '/team',
          label: t('nav_team'),
          icon: isDarkMode ? '/assets/icons/person.2.svg' : '/assets/icons/person.2.black.svg',
          isActive: pathname.startsWith('/team'),
        },
      ];

  const year = new Date().getFullYear();

  const namePart = [firstName, lastName].filter(Boolean).join(' ').trim();

  const titleText = useMemo(() => {
    if (namePart) {
      return email ? `${namePart} (${email})` : namePart;
    }
    if (email) return email;
    return 'Signed in';
  }, [namePart, email]);

  const photoSrc = (avatar?.trim() || user?.photoURL || '').trim();
  const showPhoto = Boolean(photoSrc) && !avatarBroken;
  const initials = headerAvatarInitials(firstName ?? '', lastName ?? '', email);

  useEffect(() => {
    setAvatarBroken(false);
  }, [photoSrc]);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (e) {
      console.error('Sign out failed', e);
    }
  };

  const brandBlock = (
    <>
      <img
        className={styles.brandLogo}
        src={
          isDarkMode
            ? '/assets/logos/craftin-logo-white.svg'
            : '/assets/logos/craftin-logo.svg'
        }
        alt="Craftin"
      />
      {/*
      <img
        className={styles.brandLogo}
        src="/assets/logos/crafting-icon.svg"
        alt=""
        width={32}
        height={32}
      />
      <div className={styles.brandText}>
        <span className={styles.brandTitle}>Craftin</span>
        <span className={styles.brandSub}>{org?.name ?? 'Platform'}</span>
      </div>
      */}
    </>
  );

  return (
    <div className={styles.shell}>
      <header className={styles.mobileHeader}>
        <div className={styles.brand}>{brandBlock}</div>
        <button
          type="button"
          className={styles.menuToggle}
          onClick={() => setMenuOpen((open) => !open)}
          aria-expanded={menuOpen}
          aria-controls="app-nav-drawer"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        >
          <svg className={styles.menuToggleIcon} width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            {menuOpen ? (
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            ) : (
              <>
                <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
              </>
            )}
          </svg>
        </button>
      </header>

      {menuOpen ? (
        <button
          type="button"
          className={styles.sidebarOverlay}
          onClick={() => setMenuOpen(false)}
          aria-label="Close menu"
        />
      ) : null}

      <aside
        id="app-nav-drawer"
        className={`${styles.sidebar} ${styles.sidebarEnter} ${menuOpen ? styles.sidebarOpen : ''}`}
        aria-label="Main navigation"
      >
        <div className={styles.brand}>
          {brandBlock}
        </div>
        <nav className={styles.nav}>
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              aria-current={item.isActive ? 'page' : undefined}
              onClick={() => setMenuOpen(false)}
              className={() =>
                `${styles.navItem} ${item.isActive ? styles.navItemActive : ''}`
              }
            >
              <span className={styles.navItemMain}>
                <img
                  className={styles.navIcon}
                  src={item.icon}
                  alt=""
                  width={14}
                  height={14}
                  aria-hidden
                />
                <span className={styles.navItemLabel}>{item.label}</span>
              </span>
              {item.count !== undefined ? (
                <span className="app-counter">{item.count}</span>
              ) : null}
            </NavLink>
          ))}
        </nav>
        <footer className={styles.foot}>
          <p className={styles.footCopy}>
            © Craftin {year}
            <span className={styles.version}> v{APP_VERSION}</span>
          </p>
        </footer>
      </aside>
      <div className={`${styles.mainCol} ${styles.mainColEnter}`}>
        <header className={styles.topBar}>
          <div className={styles.userRow}>
            <label
              className={styles.userAvatarUpload}
              title="Upload profile picture"
            >
              <input
                type="file"
                accept="image/*"
                className={styles.userAvatarInput}
                disabled={avatarUploading}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void uploadAvatar(file);
                  e.target.value = '';
                }}
              />
              <span className={styles.userAvatar}>
                {showPhoto ? (
                  <img
                    className={styles.userAvatarImg}
                    src={photoSrc}
                    alt=""
                    onError={() => setAvatarBroken(true)}
                  />
                ) : (
                  <span className={styles.userAvatarFallback}>{initials}</span>
                )}
                <span className={styles.userAvatarOverlay} aria-hidden>
                  {avatarUploading ? (
                    <span className={styles.userAvatarSpinner} />
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M3 8.5A1.5 1.5 0 0 1 4.5 7h2l1.2-1.8A1 1 0 0 1 8.5 4.7h7a1 1 0 0 1 .8.5L17.5 7h2A1.5 1.5 0 0 1 21 8.5v9A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5v-9Z"
                        stroke="currentColor"
                        strokeWidth="1.75"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="12.5" r="3" stroke="currentColor" strokeWidth="1.75" />
                    </svg>
                  )}
                </span>
              </span>
            </label>
            <span className={styles.userMeta} title={titleText}>
              {namePart ? (
                <>
                  <span className={styles.userName}>{namePart}</span>
                  {email ? <span className={styles.userEmail}>{email}</span> : null}
                </>
              ) : (
                <span className={styles.userName}>{email || 'Signed in'}</span>
              )}
            </span>
          </div>
          <div className={styles.topBarActions}>
            {role ? (
              <div className={styles.roleBadge}>
                <span className={styles.roleBadgeLabel}>{t('topbar_your_role')}</span>
                <span className={styles.roleBadgeValue}>{t(ROLE_LABEL_KEYS[role])}</span>
              </div>
            ) : null}
            <LanguageSwitch />
            <button
              type="button"
              className={styles.themeToggle}
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-pressed={theme === 'light'}
            >
              <ThemeToggleIcon theme={theme} />
            </button>
            <button
              type="button"
              className={styles.themeToggle}
              onClick={handleSignOut}
              aria-label="Sign out"
            >
              <img
                className={styles.themeToggleIcon}
                src={
                  isDarkMode
                    ? '/assets/icons/arrow.right.circle.svg'
                    : '/assets/icons/arrow.right.circle.black.svg'
                }
                alt=""
                width={16}
                height={16}
                aria-hidden
              />
            </button>
          </div>
        </header>
        <div className={styles.outlet}>
          <KpiExplainProvider>
            <ViewFadeIn key={pathname}>
              <Outlet />
            </ViewFadeIn>
          </KpiExplainProvider>
        </div>
        <div className={styles.mobileFooter}>
          <SiteFooter className={styles.mobileFooterInner} />
        </div>
      </div>
    </div>
  );
}
